import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateUser } from "../../../react-redux/Slicer/userSlice";
import { putData, getData } from "../../../FetchApiServices";

const GoogleTranslate = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user.users[0]);
  const [language, setLanguage] = useState("en"); // default English
  const observerRef = useRef(null);

  // --- cookie helpers (write both host-only and root-domain where possible) ---
  const getRootDomain = () => {
    const parts = window.location.hostname.split(".");
    if (parts.length >= 3) return `.${parts.slice(-2).join(".")}`; // e.g. .example.com
    return window.location.hostname; // e.g. localhost or example.com
  };

  const setCookie = (name, value, opts = {}) => {
    let str = `${name}=${value}`;
    if (opts.maxAge != null) str += `; max-age=${opts.maxAge}`;
    if (opts.expires) str += `; expires=${opts.expires.toUTCString()}`;
    if (opts.path) str += `; path=${opts.path}`;
    if (opts.domain) str += `; domain=${opts.domain}`;
    str += `; samesite=lax`;
    document.cookie = str;
  };

  const deleteCookieEverywhere = (name) => {
    const past = new Date(0);
    setCookie(name, "", { expires: past, path: "/" }); // host-only
    setCookie(name, "", { expires: past, path: "/", domain: getRootDomain() }); // root
  };

  const writeGoogTrans = (fromLang, toLang) => {
    const val = `/${fromLang}/${toLang}`;
    setCookie("googtrans", val, { path: "/" });
    setCookie("googtrans", val, { path: "/", domain: getRootDomain() });
  };

  // keep <html lang> in sync (good for a11y/SEO, won’t control Translate itself)
  useEffect(() => {
    document.documentElement.lang = language || "en";
  }, [language]);

  // 1) Hard-reset the cookie to English BEFORE loading the widget (kills stale Arabic)
  useEffect(() => {
    deleteCookieEverywhere("googtrans");
    writeGoogTrans("en", "en"); // force English as “active” language on first paint

    if (!document.getElementById("google_translate_script")) {
      window.googleTranslateElementInit = () => {
        if (window.google?.translate) {
          new window.google.translate.TranslateElement(
            {
              pageLanguage: "en",        // your source language
              autoDisplay: false,        // no auto-switching
              includedLanguages: "en,hi,fr,es,ar",
            },
            "google_translate_element"
          );
        }
      };

      const s = document.createElement("script");
      s.id = "google_translate_script";
      s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  // 2) When Redux/user preference loads, reflect it in state
  useEffect(() => {
    if (user?.language_code) setLanguage(user.language_code);
  }, [user?.language_code]);

  // 3) Apply language once the Google dropdown actually exists (race-free)
  const applyLanguageToGoogle = (lang) => {
    const combo = document.querySelector("select.goog-te-combo");
    if (combo && lang && combo.value !== lang) {
      combo.value = lang;
      combo.dispatchEvent(new Event("change"));
    }
  };

  // Observe DOM for the widget select to appear, then apply language and stop observing
  useEffect(() => {
    const combo = document.querySelector("select.goog-te-combo");
    if (combo) {
      applyLanguageToGoogle(language);
      return; // already there, no need to observe
    }
    observerRef.current?.disconnect();
    observerRef.current = new MutationObserver(() => {
      const el = document.querySelector("select.goog-te-combo");
      if (el) {
        applyLanguageToGoogle(language);
        observerRef.current?.disconnect();
      }
    });
    observerRef.current.observe(document.body, { childList: true, subtree: true });
    return () => observerRef.current?.disconnect();
  }, [language]);

  // 4) Keep the googtrans cookie in sync with the selected app language
  useEffect(() => {
    if (!language) return;
    // your “from” language is the site’s source language: en
    writeGoogTrans("en", language);
  }, [language]);

  // User changes language from your dropdown
  const handleLanguageChange = async (e) => {
    const newLang = e.target.value;
    setLanguage(newLang);
    writeGoogTrans("en", newLang);
    const result = await putData(`users/${user.user_id}`, { language_code: newLang });
    const response = await getData(`users/${user.user_id}`);
    if (result.error || response.error) {
      console.error("Error updating language:", result.error || response.error);
    } else {
      dispatch(updateUser(response.data.data[0]));
    }
  };

  return (
    <div className="custom-lang-wrapper">
      <i className="bi bi-globe2 lang-icon"></i>
      <select
        className="notranslate custom-lang-selector"
        onChange={handleLanguageChange}
        value={language}
      >
        <option value="en">English</option>
        <option value="hi">Hindi</option>
        <option value="fr">French</option>
        <option value="es">Spanish</option>
        <option value="ar">Arabic</option>
      </select>

      {/* Keep Google’s UI hidden if you only want to control via your dropdown */}
      <div id="google_translate_element" style={{ display: "none" }} />
    </div>
  );
};

export default GoogleTranslate;
